import React from "react";
import { Tabs } from "antd";
import ProtocolTable from "./ProtocolTable";
import { ProtocolsHeaderActions } from "./ProtocolsHeaderActions";
import { UnarchiveButton } from "../../shared/layout/UnarchiveButton";
import { useTranslation } from "react-i18next";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";

interface ProtocolTabsProps {
  protocols: any[];
  columns: any[];
  pagination: any;
  filters: any;
  handlePagination: (pagination: any) => Promise<void>;
  handleRowClick: (protocolId: string) => void;
  updateFilter: (key: string, value: string) => void;
  handleUnarchive: (
    protocolId: string,
    orgId: string,
    workspaceId: string
  ) => Promise<void>;
  permissions: any;
  isLoading: boolean;
  isTemplatesTabEnabled: boolean;
}

const ProtocolTabs: React.FC<ProtocolTabsProps> = ({
  protocols,
  columns,
  pagination,
  filters,
  handlePagination,
  handleRowClick,
  updateFilter,
  handleUnarchive,
  isLoading,
  isTemplatesTabEnabled,
}) => {
  const { t } = useTranslation();
  const { workspaceId, orgId } = useProtocolsRouter();
  const modifiedColumns = columns.map((col: any) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (text: any, record: any) => {
          if (filters?.tab === "archived") {
            return (
              <UnarchiveButton
                onClick={async () => {
                  await handleUnarchive(
                    record.id,
                    orgId ?? "",
                    workspaceId ?? ""
                  );
                }}
              >
                {t("protocols.unarchive")}
              </UnarchiveButton>
            );
          }
          return col.render(text, record, record.status, handleUnarchive);
        },
      };
    }
    return col;
  });

  const tabItems = [
    {
      key: "active",
      label: "Active",
      children: (
        <ProtocolTable
          protocols={protocols}
          columns={modifiedColumns}
          pagination={pagination}
          filters={filters}
          handlePagination={handlePagination}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: "archived",
      label: "Archived",
      children: (
        <ProtocolTable
          protocols={protocols}
          columns={modifiedColumns.filter((col: any) => col.key !== "actions")}
          pagination={pagination}
          filters={filters}
          handlePagination={handlePagination}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
        />
      ),
    },
  ];

  if (isTemplatesTabEnabled) {
    tabItems.push({
      key: "templates",
      label: "Templates",
      children: (
        <ProtocolTable
          protocols={protocols}
          columns={modifiedColumns.filter((col: any) => col.key !== "actions")}
          pagination={pagination}
          filters={filters}
          handlePagination={handlePagination}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
        />
      ),
    });
  }

  const activeTabKey =
    isTemplatesTabEnabled || filters.tab !== "templates"
      ? filters.tab
      : "active";

  return (
    <Tabs
      activeKey={activeTabKey}
      onChange={(key) => {
        updateFilter("tab", key);
      }}
      items={tabItems}
      tabBarExtraContent={
        <ProtocolsHeaderActions
          onSearch={(value: string) => {
            updateFilter("searchText", value);
          }}
          onFilter={(value: string) => {
            updateFilter("status", value);
          }}
          searchValue={filters?.searchText ?? ""}
          activeTab={filters.tab}
        />
      }
    />
  );
};

export default ProtocolTabs;
